// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useFunnel
 * @package @geenius/analytics/react
 * @description Provides the React funnel-analysis hook used by dashboard
 * components to evaluate ordered conversion steps against captured analytics
 * events.
 */

import type {
  AnalyticsEvent,
  FunnelResult,
  FunnelStep,
} from "@geenius/analytics";
import { analyzeFunnel } from "@geenius/analytics";
import { useCallback, useState } from "react";

/**
 * Reactive funnel-analysis state returned by {@link useFunnel}.
 *
 * @property result The most recently computed funnel result.
 * @property isAnalyzing Indicates whether the hook is computing a funnel result.
 * @property analyze Computes and stores the next funnel result from raw events.
 */
export interface UseFunnelReturn {
  result: FunnelResult | null;
  isAnalyzing: boolean;
  analyze: (events: AnalyticsEvent[]) => FunnelResult;
}

/**
 * Creates funnel-analysis state for a named conversion journey.
 *
 * @param name Human-readable funnel name used in computed result metadata.
 * @param steps Ordered conversion steps that define the funnel.
 * @returns Stateful helpers for running funnel analysis against event streams.
 */
export function useFunnel(name: string, steps: FunnelStep[]): UseFunnelReturn {
  const [result, setResult] = useState<FunnelResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analyze = useCallback(
    (events: AnalyticsEvent[]): FunnelResult => {
      setIsAnalyzing(true);
      const nextResult = analyzeFunnel(events, steps, name);
      setResult(nextResult);
      setIsAnalyzing(false);
      return nextResult;
    },
    [name, steps],
  );

  return { result, isAnalyzing, analyze };
}
